/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, cloneElement } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Paper,
  Grid,
} from "@mui/material";
import { Add, Edit, Delete, DragIndicator } from "@mui/icons-material";
import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ComponentHeader } from "components";
import { BrandButton } from "components/brand";
import {
  getAllMenuCategories,
  deleteMenuCategory,
  reorderMenuCategories,
} from "actions";
import { clearError } from "features/menuCategoriesSlice";
import { PATHNAME } from "constants/pathnames";
import { ADMIN_TEXTS } from "constants/adminTexts";
import { useDndSensors } from "hooks/useDndSensors";
import "./Menu.scss";
import { ICON_GRAY } from "constants/general";

const imageBase = process.env.REACT_APP_API_IMAGE_URL;
const DRAGGING_OPACITY = 0.8;

const SortableItem = ({ id, children }) => {
  const {
    setNodeRef,
    transform,
    transition,
    isDragging,
    attributes,
    listeners,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? DRAGGING_OPACITY : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="sortable-item"
      {...attributes}
    >
      {cloneElement(children, { dragHandleProps: listeners })}
    </div>
  );
};

const CategoryCard = ({
  category,
  onEdit,
  onDelete,
  getImageUrl,
  dragHandleProps,
}) => {
  return (
    <Card className="product-card">
      <CardContent className="card-content">
        <Box className="card-header">
          <Box {...dragHandleProps} className="drag-handle">
            <DragIndicator color="primary" />
          </Box>
          <Chip
            label={`${ADMIN_TEXTS.common.order}: ${category.order}`}
            size="small"
            color="primary"
            variant="outlined"
          />
        </Box>

        {category.image && (
          <Box className="image-container">
            <img
              src={getImageUrl(category.image)}
              alt={category.name}
              className="product-image"
            />
          </Box>
        )}

        <Typography variant="h6" component="h3" mb={0.5} mt={1} noWrap>
          {category.name}
        </Typography>

        <Box className="card-actions">
          <Box />
          <Box className="action-buttons">
            <IconButton
              size="small"
              onClick={() => onEdit(category.id)}
              className="edit-button"
            >
              <Edit color="action" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => onDelete(category.id, category.name)}
              className="delete-button"
            >
              <Delete color="action" />
            </IconButton>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export const MenuCategories = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    categories: reduxCategories,
    loading,
    reorderLoading,
    error,
  } = useSelector((state) => state.menuCategories);
  const [categories, setCategories] = useState([]);
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    id: null,
    name: "",
  });

  const sensors = useDndSensors();

  const handleEdit = (id) => {
    navigate(`${PATHNAME.menuCategories}/${id}/edit`);
  };

  const handleDeleteClick = (id, name) => {
    setDeleteDialog({
      open: true,
      id,
      name: name || ADMIN_TEXTS.menuCategories.untitledCategory,
    });
  };

  const handleDelete = () => {
    dispatch(deleteMenuCategory(deleteDialog.id));
    setDeleteDialog({ open: false, id: null, name: "" });
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      const oldIndex = categories.findIndex((c) => c.id === active.id);
      const newIndex = categories.findIndex((c) => c.id === over.id);
      const reordered = arrayMove(categories, oldIndex, newIndex).map(
        (c, i) => ({
          ...c,
          order: i + 1,
        }),
      );
      setCategories(reordered);
      dispatch(reorderMenuCategories(reordered.map((c) => c.id)));
    }
  };

  const getImageUrl = (image) => `${imageBase}${image.path}`;

  useEffect(() => {
    dispatch(getAllMenuCategories());
  }, []);

  useEffect(() => {
    if (reduxCategories) setCategories(reduxCategories);
  }, [reduxCategories]);

  useEffect(() => {
    if (error) {
      if (reduxCategories) setCategories(reduxCategories);
      dispatch(clearError());
    }
  }, [error]);

  if (loading) {
    return (
      <Box className="menu-form-loading">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="menu-categories">
      <Box className="list-header">
        <ComponentHeader title={ADMIN_TEXTS.menuCategories.pageTitle} />
        <BrandButton
          variant="primary"
          startIcon={<Add />}
          onClick={() => navigate(`${PATHNAME.menuCategories}/create`)}
        >
          {ADMIN_TEXTS.menuCategories.addCategory}
        </BrandButton>
      </Box>

      {categories.length === 0 ? (
        <Paper className="empty-state">
          <Typography variant="h6" color="text.secondary" mb={2}>
            {ADMIN_TEXTS.menuCategories.noCategories}
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            {ADMIN_TEXTS.menuCategories.noCategoriesMessage}
          </Typography>
          <BrandButton
            variant="primary"
            startIcon={<Add />}
            onClick={() => navigate(`${PATHNAME.menuCategories}/create`)}
          >
            {ADMIN_TEXTS.menuCategories.addCategory}
          </BrandButton>
        </Paper>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={categories.map((c) => c.id)}
            strategy={rectSortingStrategy}
          >
            <Box className="sortable-container">
              <Grid container spacing={2}>
                {categories.map((category) => (
                  <Grid
                    item
                    xs={12}
                    md={6}
                    lg={4}
                    key={category.id}
                    className="products-grid-item"
                  >
                    <SortableItem id={category.id}>
                      <CategoryCard
                        category={category}
                        onEdit={handleEdit}
                        onDelete={handleDeleteClick}
                        getImageUrl={getImageUrl}
                      />
                    </SortableItem>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </SortableContext>
        </DndContext>
      )}

      {reorderLoading && (
        <Box className="reorder-loading">
          <Alert severity="info" className="loading-alert">
            <CircularProgress size={16} />
            {ADMIN_TEXTS.menuCategories.reordering}
          </Alert>
        </Box>
      )}

      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, id: null, name: "" })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{ADMIN_TEXTS.menuCategories.deleteCategory}</DialogTitle>
        <DialogContent>
          <Typography>
            {ADMIN_TEXTS.menuCategories.deleteConfirm} "{deleteDialog.name}"?{" "}
            {ADMIN_TEXTS.menuCategories.deleteConfirmMessage}
          </Typography>
        </DialogContent>
        <DialogActions>
          <BrandButton
            variant="secondary"
            onClick={() => setDeleteDialog({ open: false, id: null, name: "" })}
          >
            {ADMIN_TEXTS.common.cancel}
          </BrandButton>
          <BrandButton variant="error" onClick={handleDelete}>
            {ADMIN_TEXTS.common.delete}
          </BrandButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
